import React, { useContext, useEffect, useState } from 'react';
import { Row, Col, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import 'assets/styles/common.scss';
import { LocaleContext } from 'routes/Router';
import Provider from 'assets/images/sidebar/ProviderColor.png';
import Avtar from 'assets/images/Dashboard/avtar.png';
import { FormattedMessage } from 'react-intl';
import CustomPopover from 'components/common/CustomPopover';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getUserRole } from 'utils/general';
import { useDispatch, useSelector } from 'react-redux';
import { getDashboardProviderDataCallAPI } from 'store/ProviderAdmin/dashboard/action';
import moment from 'moment';
import { cancelAppointmentProviderAdminCallAPI } from 'store/ProviderAdmin/cancelAppointment/action';
import { ShimmerBox, ShimmerCircle } from 'components/common/Shimmer';
import Ptext from 'components/common/Ptext';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import {
  providerAdminAppointments,
  providerAdminBranches,
  providerAdminCustomer,
  providerAdminSpecialists,
  superAdminAppointments,
} from 'routes/AppUrls';
import './dashboard.scss';
import { setPageTitle } from 'store/pageTitle/action';

// ─── Tooltip style ────────────────────────────────────────────────────────────
const DashboardTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow placement="top" classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#000000',
    color: '#FAFAF9',
    fontSize: '12px',
    fontWeight: 500,
    padding: '10px 12px',
    borderRadius: '10px',
    maxWidth: 220,
    boxShadow: '0px 8px 24px rgba(0,0,0,0.15)',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#000000',
  },
}));

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({
  label,
  value,
  bg,
  actionIcon,
  onAction,
  loading,
  tooltipText,
}) => {
  if (loading) {
    return (
      <div
        className={`${bg} rounded-2xl p-4 h-[200px] flex flex-col justify-between`}
      >
        <div className="flex items-center justify-between">
          <ShimmerBox width="110px" height="13px" />
          <ShimmerBox width="16px" height="16px" />
        </div>
        <div className="flex items-end justify-between">
          <ShimmerBox width="44px" height="34px" />
          <ShimmerCircle size="38px" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className={`stat-card ${bg} rounded-2xl p-4 h-[200px] flex flex-col justify-between relative`}
      >
        <div className="flex items-start justify-between gap-2">
          <span
            className="text-[16px] font-medium leading-[24px] text-[#0C0A09]"
            style={{
              fontFamily: 'Poppins',
              letterSpacing: '0.16px',
            }}
          >
            {label}
          </span>
          <DashboardTooltip title={tooltipText || label}>
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                width: '20px',
                height: '20px',
                color: '#44403C',
                mt: '1px',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            />
          </DashboardTooltip>
        </div>
        <div>
          <span className="text-[32px] font-semibold text-gray-900 leading-none">
            {value ?? 0}
          </span>
        </div>
      </div>
      <button
        onClick={onAction}
        className="absolute bottom-1 right-1 w-[48px] h-[48px] rounded-full bg-[#1A1A1A] text-white flex items-center justify-center hover:bg-black transition-colors shadow-md z-90"
      >
        {actionIcon === 'arrow' ? (
          <NorthEastIcon sx={{ fontSize: 17 }} />
        ) : (
          <AddIcon sx={{ fontSize: 20 }} />
        )}
      </button>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ProviderAdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { locale } = useContext(LocaleContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const userRole = getUserRole();
  const [viewData, setViewData] = useState('');

  const dashboardResponse = useSelector(
    (state) => state?.getDashboardProviderDataReducer?.data?.data
  );
  const dashboardLoading = useSelector(
    (state) => state?.getDashboardProviderDataReducer?.loading
  );
  const [dashboardData, setDashboardData] = useState(dashboardResponse ?? []);

  const handlePopoverClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setViewData(item);
  };
  const handlePopoverClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const PopoverData = [
    {
      label: <FormattedMessage id="Delete" />,
      icon: (
        <DeleteOutlinedIcon style={{ marginRight: '5px', marginTop: '-5px' }} />
      ),
      onClick: () => handleCancelAppointment(viewData),
    },
  ];

  useEffect(() => {
    dispatch(setPageTitle('Dashboard'));

    getDashboardDataApi();
  }, []);

  const handleCancelAppointment = (data) => {
    dispatch(
      cancelAppointmentProviderAdminCallAPI(
        {},
        {
          id: data?.id,
          run: true,
          handleFunction: () => getDashboardDataApi(),
          navigate,
        }
      )
    );
  };

  const getDashboardDataApi = () => {
    dispatch(
      getDashboardProviderDataCallAPI(
        {},
        { run: true, handleFunction: (res) => handleSuccess(res), navigate }
      )
    );
  };

  const handleSuccess = (res) => {
    setDashboardData(res?.data?.data);
    setAnchorEl(null);
  };

  const createCard = ({
    label,
    value,
    onAction,
    tooltipText,
    bg = 'bg-[#FAE5C3]',
  }) => ({
    label: <FormattedMessage id={label} />,
    value,
    bg,
    actionIcon: 'arrow',
    onAction,
    tooltipText,
  });

  const getStatCards = () => {
    const cards = [];

    const countData = dashboardData?.count_data;

    if (userRole === 'company-admin') {
      cards.push(
        createCard({
          label: 'Total Branches',
          value: countData?.total_branches?.branchcount,
          bg: 'bg-[#F9D6D3]',
          onAction: () => navigate(providerAdminBranches(locale)),
          tooltipText:
            'View and manage all your salon branches from one place.',
        })
      );
    }

    if (userRole !== 'specialist-admin') {
      cards.push(
        createCard({
          label: 'Total Specialists',
          value: countData?.total_specialist,
          bg: 'bg-[#DDD9F0]',
          onAction: () => navigate(providerAdminSpecialists(locale)),
          tooltipText:
            'Add team members to your profile to track your staff count here.',
        })
      );
    }

    cards.push(
      createCard({
        label: 'Total Customers',
        value: countData?.total_customuser,
        bg: 'bg-[#D4EBD9]',
        onAction: () => navigate(providerAdminCustomer(locale)),
        tooltipText:
          'Your customer base will grow and be displayed here once you start receiving bookings.',
      })
    );

    const appointmentConfig = {
      'specialist-admin': {
        value: countData?.total_appointment,
        route: providerAdminAppointments(locale),
      },
      'salon-admin': {
        value: countData?.appointment_data?.total_appointment,
        route: providerAdminAppointments(locale),
      },
      'branch-company': {
        value: countData?.appointment_data?.total_appointment,
        route: providerAdminAppointments(locale),
      },
      'company-admin': {
        value: countData?.total_appointment?.total_appointments,
        route: superAdminAppointments(locale),
      },
    };

    const appointment = appointmentConfig[userRole];

    if (appointment) {
      cards.push(
        createCard({
          label: 'Total Appointments',
          value: appointment.value,
          onAction: () => navigate(appointment.route),
          tooltipText:
            'Your data will appear here as soon as you receive your first booking.',
        })
      );
    }

    return cards;
  };
  const statCards = getStatCards();
  const appointmentCount = dashboardData?.appointment_today?.length ?? 0;

  return (
    <Row className="bg-[#F0EDEB] m-0 p-0">
      <Col
        className={
          locale === 'en'
            ? 'commonEnContainer lg:ml-left'
            : 'commonArContainer lg:mr-right'
        }
      >
        {/* <Ptext variant="heading" className="m-0">
          <FormattedMessage id="Dashboard" />
        </Ptext> */}

        {/* ── Stat cards ────────────────────────────────────────── */}
        <div className="mt-3 bg-white rounded-2xl p-4 pb-8 overflow-visible">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {statCards.map((card, idx) => (
              <StatCard key={idx} {...card} loading={dashboardLoading} />
            ))}
          </div>
        </div>

        {/* ── Appointments Today Card ───────────────────────────── */}
        <div className="mt-4 bg-white rounded-3xl overflow-hidden mb-4">
          {/* FIX 3 — reduced pb-4 → pb-2 to tighten gap before table header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <Ptext variant="table">
              <FormattedMessage id="Appointments Today" />
              {': '}
              <span>{dashboardLoading ? '–' : appointmentCount}</span>
            </Ptext>
            <button
              onClick={() => navigate(providerAdminAppointments(locale))}
              className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-50"
              title="View all appointments"
            >
              <NorthEastIcon sx={{ fontSize: 20 }} />
            </button>
          </div>

          {/* FIX 2 — replaced `listHeader` class (grey bg) with explicit white bg + subtle border */}
          {(dashboardLoading || appointmentCount > 0) && (
            <Row className="m-0 px-4 py-2 flex items-center bg-white border-b border-gray-100">
              <Col lg={2} md={6} sm={12}>
                <Ptext variant="table_header" className="m-0">
                  <FormattedMessage id="Booking ID" />
                </Ptext>
              </Col>
              <Col
                lg={`${userRole === 'specialist-admin' ? 3 : 2}`}
                md={6}
                sm={12}
              >
                <Ptext variant="table_header" className="m-0">
                  <FormattedMessage id="Customer" />
                </Ptext>
              </Col>
              {(userRole === 'salon-admin' ||
                userRole === 'branch-company') && (
                <Col lg={2} md={6} sm={12}>
                  <Ptext variant="table_header" className="m-0">
                    <FormattedMessage id="Specialist" />
                  </Ptext>
                </Col>
              )}
              {userRole === 'company-admin' && (
                <Col lg={2} md={6} sm={12}>
                  <Ptext variant="table_header" className="m-0">
                    <FormattedMessage id="Provider" />
                  </Ptext>
                </Col>
              )}
              <Col
                lg={`${userRole === 'specialist-admin' ? 3 : 2}`}
                md={6}
                sm={12}
              >
                <Ptext variant="table_header" className="m-0">
                  <FormattedMessage id="Service Location" />
                </Ptext>
              </Col>
              <Col lg={2} md={6} sm={12}>
                <Ptext variant="table_header" className="m-0">
                  <FormattedMessage id="Date & Time" />
                </Ptext>
              </Col>
              <Col lg={2} md={6} sm={12}>
                <Ptext variant="table_header" className="m-0">
                  <FormattedMessage id="Actions" />
                </Ptext>
              </Col>
            </Row>
          )}

          {/* ── Table body / empty state ──────────────────────── */}
          <Row className="bc-FFFFFF m-0 p-0">
            {dashboardLoading ? (
              <>
                {[1, 2, 3, 4, 5].map((item) => (
                  <Row
                    key={item}
                    className="m-0 p-0 flex items-center listAppointment"
                  >
                    <Col lg={2} md={6} sm={12}>
                      <ShimmerBox width="100px" height="12px" />
                    </Col>
                    <Col
                      lg={`${userRole === 'specialist-admin' ? 3 : 2}`}
                      md={6}
                      sm={12}
                    >
                      <div className="flex items-center gap-2">
                        <ShimmerCircle size="40px" />
                        <div>
                          <ShimmerBox width="120px" height="12px" />
                          <ShimmerBox
                            width="100px"
                            height="12px"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </Col>
                    {(userRole === 'salon-admin' ||
                      userRole === 'branch-company' ||
                      userRole === 'company-admin') && (
                      <Col lg={2} md={6} sm={12}>
                        <div className="flex items-center gap-2">
                          <ShimmerCircle size="40px" />
                          <div>
                            <ShimmerBox width="120px" height="12px" />
                            <ShimmerBox
                              width="100px"
                              height="12px"
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </Col>
                    )}
                    <Col
                      lg={`${userRole === 'specialist-admin' ? 3 : 2}`}
                      md={6}
                      sm={12}
                    >
                      <div className="flex items-center gap-2">
                        <ShimmerBox width="24px" height="24px" />
                        <ShimmerBox width="150px" height="14px" />
                      </div>
                      <ShimmerBox
                        width="200px"
                        height="12px"
                        className="mt-1"
                      />
                    </Col>
                    <Col lg={2} md={6} sm={12}>
                      <ShimmerBox width="100px" height="12px" />
                      <ShimmerBox width="80px" height="12px" className="mt-1" />
                    </Col>
                    <Col lg={2} md={6} sm={12}>
                      <ShimmerBox width="30px" height="20px" />
                    </Col>
                  </Row>
                ))}
              </>
            ) : appointmentCount > 0 ? (
              dashboardData.appointment_today.map((key, index) => (
                <Row
                  key={index}
                  className="m-0 p-0 flex items-center listAppointment"
                >
                  <Col lg={2} md={6} sm={12}>
                    <Ptext variant="table_content" className="m-0 px-4">
                      {key?.booking_id}
                    </Ptext>
                  </Col>
                  <Col
                    lg={`${userRole === 'specialist-admin' ? 3 : 2}`}
                    md={6}
                    sm={12}
                  >
                    <Stack
                      direction="horizontal"
                      gap={2}
                      className="items-center"
                    >
                      <img
                        src={key?.customers_data?.customers_image || Avtar}
                        alt="icon"
                        style={{
                          height: '40px',
                          width: '40px',
                          borderRadius: '100px',
                        }}
                      />
                      <div>
                        <Ptext
                          variant="table_content"
                          className="m-0 font-bold"
                        >
                          {key?.customers_data?.customers_name}
                        </Ptext>
                        <Ptext variant="table_content" className="m-0">
                          {key?.customers_data?.customers_id}
                        </Ptext>
                      </div>
                    </Stack>
                  </Col>
                  {(userRole === 'salon-admin' ||
                    userRole === 'branch-company' ||
                    userRole === 'company-admin') && (
                    <Col lg={2} md={6} sm={12}>
                      <Stack
                        direction="horizontal"
                        gap={2}
                        className="items-center"
                      >
                        <img
                          src={key?.provider_data?.provider_image || Avtar}
                          alt="icon"
                          style={{
                            height: '40px',
                            width: '40px',
                            borderRadius: '100px',
                          }}
                        />
                        <div>
                          <Ptext
                            variant="table_content"
                            className="m-0 font-bold"
                          >
                            {key?.provider_data?.name}
                          </Ptext>
                          <Ptext variant="table_content" className="m-0">
                            {key?.provider_data?.provider_id}
                          </Ptext>
                        </div>
                      </Stack>
                    </Col>
                  )}
                  <Col
                    lg={`${userRole === 'specialist-admin' ? 3 : 2}`}
                    md={6}
                    sm={12}
                  >
                    <Stack
                      direction="horizontal"
                      gap={2}
                      className="items-center"
                    >
                      <img
                        src={Provider}
                        alt="icon"
                        style={{ height: '24px', width: '24px' }}
                      />
                      <Ptext variant="table_content" className="m-0 font-bold">
                        {key?.service_location}
                      </Ptext>
                    </Stack>
                    <Ptext variant="table_content" className="m-0">
                      {key?.delivery_address}
                    </Ptext>
                  </Col>
                  <Col lg={2} md={6} sm={12}>
                    <Ptext variant="table_content" className="m-0">
                      {key?.slot_date != null
                        ? moment(key?.slot_date).format('DD MMM YYYY')
                        : ''}
                    </Ptext>
                    <Ptext variant="table_content" className="m-0">
                      {key?.slot_start_time != null
                        ? moment(key?.slot_start_time).format('h:mm A')
                        : ''}
                    </Ptext>
                  </Col>
                  <Col lg={2} md={6} sm={12}>
                    <div>
                      <MoreVertIcon
                        aria-describedby={id}
                        style={{ fill: '#3C1053', cursor: 'pointer' }}
                        onClick={(e) => handlePopoverClick(e, key)}
                      />
                      <CustomPopover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        handleClose={handlePopoverClose}
                        options={PopoverData}
                      />
                    </div>
                  </Col>
                </Row>
              ))
            ) : (
              /* FIX 1 — empty state height increased via minHeight */
              <div
                className="flex flex-col items-center justify-center px-4"
                style={{ minHeight: '380px' }}
              >
                <Ptext variant="body" className="font-weight-500">
                  <FormattedMessage id="No appointments today" />
                </Ptext>
                <Ptext variant="small" className="font-weight-400">
                  <FormattedMessage id="Your calendar is clear." />
                </Ptext>
              </div>
            )}
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export default ProviderAdminDashboard;
